import React from "react";
import GaugeComponent from "react-gauge-component";
import Card from "components/card";

const RouteGauges = ({
  gaugeInfo,
  latestValues,
  isPlaying,
  currentIndex,
  mapInfo,
}) => {
  // Function to get the current value based on playback or initial load
  const getCurrentGaugeValue = (gauge) => {
    if (isPlaying && mapInfo[currentIndex]) {
      // Use the value from the current playback index
      return mapInfo[currentIndex][gauge.tableval] || 0;
    }
    // Use the latest value from the data
    return latestValues[gauge.tableval] || 0;
  };

  if (!Array.isArray(gaugeInfo) || gaugeInfo.length === 0) {
    return <div>No gauge information available.</div>;
  }

  const renderSimpleGauge = (currentValue, min, max) => (
    <GaugeComponent
      value={currentValue}
      minValue={min}
      maxValue={max}
      arc={{
        subArcs: [
          { limit: 20, color: "#EA4228", showTick: true },
          { limit: 40, color: "#F58B19", showTick: true },
          { limit: 60, color: "#F5CD19", showTick: true },
          { limit: 100, color: "#5BE12C", showTick: true },
        ],
      }}
      pointer={{ type: "blob", animationDelay: 0 }}
    />
  );

  const renderSimpleInterpolatedGauge = (currentValue, min, max) => (
    <GaugeComponent
      value={currentValue}
      minValue={min}
      maxValue={max}
      arc={{
        subArcs: [
          { limit: 20, color: "#EA4228", showTick: true },
          { limit: 40, color: "#F58B19", showTick: true },
          { limit: 60, color: "#F5CD19", showTick: true },
          { limit: 100, color: "#5BE12C", showTick: true },
        ],
      }}
      pointer={{ type: "blob", animationDelay: 0 }}
    />
  );

  const renderReactGauge = (currentValue, min, max) => (
    <GaugeComponent
      value={currentValue}
      minValue={min}
      maxValue={max}
      type="semicircle"
      arc={{
        colorArray: ["#EA4228", "#5BE12C"],
        subArcs: [{ limit: 10 }, { limit: 30 }, {}, {}, {}],
        padding: 0.02,
        width: 0.3,
      }}
      pointer={{ elastic: true, animationDelay: 0 }}
    />
  );

  const renderTemperatureGauge = (currentValue, min, max) => (
    <GaugeComponent
      type="semicircle"
      arc={{
        width: 0.2,
        padding: 0.005,
        cornerRadius: 1,
        subArcs: [
          {
            limit: 15,
            color: "#EA4228",
            showTick: true,
            tooltip: { text: "Too low temperature!" },
          },
          {
            limit: 17,
            color: "#F5CD19",
            showTick: true,
            tooltip: { text: "Low temperature!" },
          },
          {
            limit: 28,
            color: "#5BE12C",
            showTick: true,
            tooltip: { text: "OK temperature!" },
          },
          {
            limit: 30,
            color: "#F5CD19",
            showTick: true,
            tooltip: { text: "High temperature!" },
          },
          { color: "#EA4228", tooltip: { text: "Too high temperature!" } },
        ],
      }}
      pointer={{
        color: "#345243",
        length: 0.8,
        width: 15,
      }}
      labels={{
        valueLabel: { formatTextValue: (value) => value + "ºC" },
        tickLabels: {
          type: "outer",
          defaultTickValueConfig: {
            formatTextValue: (value) => value + "ºC",
            style: { fontSize: 10 },
          },
          ticks: [{ value: 13 }, { value: 22.5 }, { value: 32 }],
        },
      }}
      value={currentValue}
      minValue={min}
      maxValue={max}
    />
  );

  return (
    <Card extra="rounded-[20px] p-3 relative">
      <div className="grid h-full grid-cols-1 gap-5 md:grid-cols-4">
        {gaugeInfo.map((gauge) => {
          const currentValue = getCurrentGaugeValue(gauge);

          const max = gauge.gaugemaxval;
          const min = gauge.gaugeminval;
          const gaugetype = gauge.gaugetype; // Default to semicircle if not specified

          return (
            <div key={gauge.gaugename} className="gauge relative">
              <p className="mb-2 text-center font-semibold">
                {gauge.gaugename}: {currentValue}
              </p>

              {/* Render Gauge Component with design based on gaugetype */}
              {gaugetype === "Simple Gauge (w/ tooltips)"
                ? renderSimpleGauge(currentValue, min, max)
                : gaugetype === "Simple interpolated Gauge"
                ? renderSimpleInterpolatedGauge(currentValue, min, max)
                : gaugetype === "ReactGauge"
                ? renderReactGauge(currentValue, min, max)
                : renderTemperatureGauge(currentValue, min, max)}
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default React.memo(RouteGauges);
